// Project rules for agent sessions (spec: same order as the collector). Folder rules hold local paths and stay on
// their Mac, so the server and the web know git remote and branch rules only.

export interface SessionRule {
  kind: string;
  pattern: string;
  priority: number;
  project: string;
}

export interface RuleSubject {
  remote: string | null;
  branch: string | null;
}

/** Same normalisation as the collector's GitInfo.normalizeRemote: `git@github.com:a/b.git` → `github.com/a/b`. */
export function normalizeRemote(remote: string): string | null {
  let s = remote.trim();
  if (!s) return null;
  const scheme = s.indexOf('://');
  if (scheme >= 0) s = s.slice(scheme + 3);
  const at = s.indexOf('@');
  if (at >= 0 && !s.slice(0, at).includes('/')) s = s.slice(at + 1);
  const colon = s.indexOf(':');
  if (colon >= 0) {
    const host = s.slice(0, colon);
    let rest = s.slice(colon + 1);
    const slash = rest.indexOf('/');
    if (slash >= 0 && /^\d*$/.test(rest.slice(0, slash))) rest = rest.slice(slash + 1);
    s = `${host}/${rest}`;
  }
  if (s.endsWith('.git')) s = s.slice(0, -4);
  while (s.endsWith('/')) s = s.slice(0, -1);
  return s.toLowerCase();
}

type GlobToken = '*' | '?' | number | { negated: boolean; ranges: [number, number][] };

function globTokens(pattern: string): GlobToken[] | null {
  const chars = Array.from(pattern);
  const tokens: GlobToken[] = [];
  for (let i = 0; i < chars.length; i++) {
    const char = chars[i]!;
    if (char === '*' || char === '?') tokens.push(char);
    else if (char === '\\') {
      if (++i === chars.length) return null;
      tokens.push(chars[i]!.codePointAt(0)!);
    } else if (char === '[') {
      let j = i + 1;
      const negated = chars[j] === '!' || chars[j] === '^';
      if (negated) j++;
      const ranges: [number, number][] = [];
      while (j < chars.length && (chars[j] !== ']' || ranges.length === 0)) {
        let start = chars[j++]!;
        if (start === '\\') {
          if (j === chars.length) return null;
          start = chars[j++]!;
        }
        const low = start.codePointAt(0)!;
        let high = low;
        if (chars[j] === '-' && j + 1 < chars.length && chars[j + 1] !== ']') {
          j++;
          let end = chars[j++]!;
          if (end === '\\') {
            if (j === chars.length) return null;
            end = chars[j++]!;
          }
          high = end.codePointAt(0)!;
        }
        ranges.push([low, high]);
      }
      if (chars[j] !== ']') return null;
      tokens.push({ negated, ranges });
      i = j;
    } else tokens.push(char.codePointAt(0)!);
  }
  return tokens;
}

/** Portable fnmatch without flags (spec §13), with bounded pattern × text work instead of regex backtracking. */
export function globMatch(pattern: string, text: string): boolean {
  const tokens = globTokens(pattern);
  if (!tokens) return false;
  const points = Array.from(text, (char) => char.codePointAt(0)!);
  let previous = new Uint8Array(points.length + 1);
  let next = new Uint8Array(points.length + 1);
  previous[0] = 1;
  for (const token of tokens) {
    next.fill(0);
    if (token === '*') next[0] = previous[0]!;
    for (let j = 1; j <= points.length; j++) {
      if (token === '*') next[j] = previous[j] || next[j - 1] ? 1 : 0;
      else if (previous[j - 1]) {
        const point = points[j - 1]!;
        const matches = token === '?' || (typeof token === 'number' ? token === point
          : token.negated !== token.ranges.some(([low, high]) => low <= point && point <= high));
        next[j] = matches ? 1 : 0;
      }
    }
    [previous, next] = [next, previous];
  }
  return previous[points.length] === 1;
}

export function ruleMatches(rule: Pick<SessionRule, 'kind' | 'pattern'>, subject: RuleSubject): boolean {
  if (rule.kind === 'remote') {
    const remote = subject.remote ? normalizeRemote(subject.remote) : null;
    const pattern = normalizeRemote(rule.pattern);
    return !!remote && !!pattern && (remote === pattern || remote.startsWith(`${pattern}/`));
  }
  if (rule.kind === 'branch') return !!subject.branch && globMatch(rule.pattern, subject.branch);
  return false;
}

/** Highest priority first, remote before branch, then the longest pattern. */
export function matchRules(rules: readonly SessionRule[], subject: RuleSubject): string | null {
  const rank = (kind: string) => (kind === 'remote' ? 1 : 2);
  const ordered = [...rules].sort((a, b) => b.priority - a.priority || rank(a.kind) - rank(b.kind) || b.pattern.length - a.pattern.length);
  return ordered.find((rule) => ruleMatches(rule, subject))?.project ?? null;
}
